import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";

import styles from "./TagSelector.module.css";

import { getTag, getTags, deleteTag, getTagsWithAttachedMedia, searchLibrary, putTagOnMedia } from "../../api/Api.js";

import { Tag } from "../Tag/Tag.js";
import { TagHeader } from "../Tag/TagHeader.js";

import { TagBook } from "../BookTypes/TagBook/TagBook.js";

import { Loader } from "../Loader/Loader.js";
import { useMediaUpdate } from "../../contexts/MediaContext.js";
import ErrorHandler from "../../helpers/ErrorHandler.js";
import { BookSuggestion } from "../SuggestionTypes/BookSuggestion/BookSuggestion.js";

export const TagSelector = () => {

    // ----------------------------------------------------- STATE AND HOOKS
    let token = localStorage.getItem("token");
    let selectedTagID = useLocation().pathname.split("/")[2];
    let resetPath = "/tags";
    let timeOfBackendSearch = Date.now();

    const mediaUpdater = useMediaUpdate();

    const [isLoading, setIsLoading] = useState(true);
    const [isAdding, setIsAdding] = useState(false);

    const [searchString, setSearchString] = useState(null);
    const [searchResults, setSearchResults] = useState(null);
    const [selectedMedia, setSelectedMedia] = useState(null);

    const [selectedTag, setSelectedTag] = useState(null);

    const [allTagsList, setAllTagsList] = useState();
    const [allTagsNameDict, setAllTagsNameDict] = useState({});
    const [allTagsDict, setAllTagsDict] = useState(null);

    const allTagMediaObject = (selectedTagID !== null && allTagsDict !== null && allTagsDict[selectedTagID] !== null) ?
        makeTagMediaObjects(allTagsDict[selectedTagID])
        :
        makeTagMediaObjects(selectedTag);
    const allTagHeaderObjects = makeTagHeaderObjects();
    const searchSuggestions = makeSearchResults();

    useEffect(() => {
        // first render, get tags
        fetchTags();
        fetchAttachedMedia(); // background task to process all tags

        // resets contexts to avoid flash of previous book/tvshow
        mediaUpdater({});
    }, []);


    useEffect(() => {
        // runs when selected tag changes AND when avalible data changes
        if (allTagsDict === null || allTagsDict[selectedTagID] === null) {
            setIsLoading(true);
            fetchTag(selectedTagID);
        }
    }, [selectedTagID])

    useEffect(() =>{
        if (selectedMedia === null) return;
        else {
            addMediaToTag();
        }
    },[selectedMedia])


    // ----------------------------------------------------- PRE-RENDER

    async function fetchTags() {
        // gets all tags WITHOUT associated books and tvshows for speed
        await getTags(token)
            .then((tags) => {
                // turns tags and tag names into easily traverable dictionary
                let temp = {}
                let tempNames = {}
                for (let i = 0; i < tags.length; i++) {
                    temp[tags[i].tagID] = null; // dictionary of tagItems
                    tempNames[tags[i].tagID] = tags[i].tagValue; // dictionary of tagValue 
                }

                // sets vars
                setAllTagsList(tags);
                setAllTagsDict(temp);
                setAllTagsNameDict(tempNames);

                setIsLoading(false);
            })
            .catch((error) => {
                console.log(error, "failed to fetch tags");
                ErrorHandler(error);
            })
    }

    async function fetchTag(fetchTagID) {
        // gets an individual tagItem
        if (fetchTagID === null || fetchTagID === undefined) return;

        await getTag(fetchTagID, token)
            .then((tag) => {
                // saves found data to dict if dict exists
                if (allTagsDict) {
                    // tag dictionary already set up
                    let temp = allTagsDict;
                    temp[tag.tagID] = tag;
                    setAllTagsDict(temp);

                } else {
                    // no tag dictionary to add tag to. 
                    // Should ONLY occur when tag page is navigated to through a tag or is refreshed with tag already selected

                    setSelectedTag(tag);
                }

                setIsLoading(false)
            })
            .catch((error) => {
                console.log(error, "failed to get ", selectedTagID);
                ErrorHandler(error);
            })
    }

    async function fetchAttachedMedia() {
        // gets all tags WITH asscociated media, processes in background
        await getTagsWithAttachedMedia(token)
            .then((tags) => {
                // adds all tag data to dict
                let temp = {}
                for (let i = 0; i < tags.length; i++) {
                    if (temp[tags[i].tagID] !== null) {
                        // adds unseen tags to dictonary
                        // does NOT add seen ones becuase that means user could have added media to tag from this view
                        temp[tags[i].tagID] = tags[i];
                    }
                   
                }
                setAllTagsDict(temp);
            })
            .catch((error) => {
                console.log(error, "failed to get all tags with attached media");
                ErrorHandler(error);
            })
    }

    async function deleteTagFromDB() {
        // deletes tag from database

        await deleteTag(selectedTagID, token)
            .then(() => {
                fetchTags();
            })
            .catch((error) => {
                console.log(error, "failed to delete tag");
                ErrorHandler(error);
            });
    }

    async function searchForTitle() {
        //searches back for title
        let searchObj = {
            searchString : searchString,
            searchType : "title"
        }

        await searchLibrary(searchObj, token)
            .then((searchResults) => {
                setSearchResults(searchResults);
            })
            .catch((error) => {
                console.log("failed to search by title", error);
                ErrorHandler(error);
            })
    }

    async function addMediaToTag() {
        // adds media to tag
        await putTagOnMedia(selectedMedia.usermediaid, selectedTagID, token)
            .then((media) => {
                setSelectedMedia(null);

                // adds media to dict
                let temp = allTagsDict;
                temp[selectedTagID].media.push(media)

                setAllTagsDict(temp);
            })
            .catch((error) => {
                console.log("failed to add tag to media", error);
                ErrorHandler(error);

                setSelectedMedia(null);
            })
    }

    function handleDeleteTag() {
        // makes call to backend  
        deleteTagFromDB()
    }

    function makeTagHeaderObjects() {
        // makes tag objects for top of page
        let tags = allTagsList;
        if (tags?.length > 0) {
            let tempTagsList = [];
            let tempLetterList = [];
            let lastHeader = null;

            for (let i = 0; i < tags.length; i++) {
                // adds header for each letter seen to array
                if (!(/[a-zA-Z]/).test(tags[i].tagValue.slice(0, 1))) {
                    // group tags that do not start with a letter 
                    if (lastHeader !== "num") {
                        // adds tag header to letter group
                        tempLetterList.push(<div key={"num"}>
                            <TagHeader headerValue={"123!?"} />
                        </div>);
                        // updates last seen header
                        lastHeader = "num";
                    }
                }
                else if (tags[i].tagValue.slice(0, 1) != lastHeader) {
                    // groups tag by first letter
                    if (lastHeader !== null) {
                        // pushes letter group to larger array if it is not the first time through the loop
                        tempTagsList.push(<div className={styles.letterGroup} key={"letterGroup:" + lastHeader}>
                            {tempLetterList}
                        </div>);
                        tempLetterList = [];
                    }

                    // adds tag header to letter group
                    tempLetterList.push(<div key={tags[i].tagValue.slice(0, 1)}>
                        <TagHeader headerValue={tags[i].tagValue.slice(0, 1).toUpperCase()} />
                    </div>);
                    // updates last seen header
                    lastHeader = tags[i].tagValue.slice(0, 1);
                }
                // adds tag to letter group
                tempLetterList.push(<div className={styles.tag} key={tags[i].tagID}>
                    <Tag tagItem={tags[i]} selected={selectedTagID} nextTag={tags[0].tagID} />
                </div>);
            }

            // adds all groups to main array
            return ([tempTagsList, tempLetterList]);
        } else if (!isLoading) {
            // empty message for no tags
            return (<div className={styles.emptyShelf}>
                <h1>This shelf is currently empty </h1>
            </div>)
        }
    }

    function makeTagMediaObjects(tag) {
        // makes tag book/tvshow objects
        if (tag === undefined || tag === null) return;

        let tempTagObjectList = [];

        if (tag.media !== null) {
            // sorts books by author and makes book obejcts
            let sortedBooks = tag.media.sort(function (a, b) {
                if (a.creators === null || b.creators === null || a.creators.length === 0 || b.creators.length === 0) {
                    return -1;
                }
                if (a.creators[0]?.last > b.creators[0]?.last) {
                    return 1;
                }
                if (a.creators[0]?.last < b.creators[0]?.last) {
                    return -1;
                }
                return 0;
            });

            // makes objects
            for (let i = 0; i < sortedBooks.length; i++) {
                tempTagObjectList.push(
                    <TagBook mediaItem={sortedBooks[i]} key={sortedBooks[i].mediaid} />
                )
            }
        }

        // saving
        if (tempTagObjectList?.length === 0) {
            // NO media
            return (
                <div className={styles.emptyMessageContainer}>
                    <h4 className={styles.emptyMessage}> There are no items with this tag</h4>
                    <Link to={resetPath} ><button className={styles.removeTagButton} onClick={handleDeleteTag}>Delete Tag</button></Link>
                </div>
            )
        } else {
            // SOME media
            return (tempTagObjectList);
        }
    }

    function makeSearchResults() {
        // makes search results

        if (searchResults === null) return;
        else {
            let temp = [];
            for (let i = 0; i < searchResults.length; i++) {
                temp.push (
                    <BookSuggestion mediaItem={searchResults[i]} setSelectedBook={setSelectedMedia} key={"suggestion"+i}/>
                )
            }

            console.log(searchResults, temp)
            return temp;
        }
    }

    const toggleAdding = () => {
        // toggles is adding state
        setIsAdding(!isAdding);

        if (isAdding === false) {
            setSearchString("")
            setSearchResults(null)
        }
    }

    const changeSearchString = (event) => {
        // catches changes to search string
        setSearchString(event.target.value);

        // searches backend if enough time has elapsed
        let currentTime = Date.now();
        if ((currentTime - timeOfBackendSearch) / 1000 >= 0.2) {
            timeOfBackendSearch = Date.now();
            searchForTitle(event.target.value);
        }
    }


    // ----------------------------------------------------- RENDER

    return (
        <section className={styles.container}>
            <h1 className={styles.sectionTitle}>Tags</h1>
            <div className={styles.content}>
                <div className={styles.tagGridContainer}>
                    {allTagHeaderObjects}
                </div>
            </div>
            {selectedTagID !== undefined ?
                <div className={styles.selectedBooksContainer}>
                    <div className={styles.titleAndPlus}>
                        <h1 className={styles.sectionTitle}>Media tagged: {allTagsNameDict[selectedTagID]}</h1>
                        <button className={styles.addButton} onClick={toggleAdding}>
                            <img src="/assets/images/plusIcon.svg"
                                alt="plus icon"
                                title="Add Media To Tag"
                                className={styles.plusIcon} />
                        </button>
                    </div>

                    <div className={styles.content}>
                        {isLoading ?
                            <div className={styles.loaderForMediaTagged}>
                                <Loader />
                            </div>
                            :
                            <div>
                                {isAdding ?
                                        <div>
                                            <div className={styles.addingContainer}>
                                                <input type="text" 
                                                        placeholder="Search by Title"
                                                        className={styles.input}
                                                        onChange={changeSearchString}/>
                                                <div className={styles.searchSuggestions}>
                                                    {searchSuggestions}
                                                </div>
                                            </div>
                                            <div className={styles.bookGridContainer}>
                                                {allTagMediaObject}
                                            </div>
                                        </div>
                                        :
                                        <div className={styles.bookGridContainer}>
                                                {allTagMediaObject}
                                        </div>
                                }
                            </div>
                        }
                    </div>
                </div> : null
            }
        </section>
    )
}